import { type MarketingLocale, useLocalized } from '~/lib/marketing-locale'
import { ScrollReveal } from './scroll-reveal'
import { TruchetField } from './truchet-field'

/*
 * Propostas, the outbound funnel. Pipeline kanban on the left as the
 * centerpiece, with the public proposal peeking out from behind on the
 * right. Below, a horizontal lifecycle strip and the service catalog as a
 * wide pricing table. No 1+3 card pattern.
 *
 * Copy is locale-keyed — see the note in `crm-section.tsx`. The English
 * catalog prices per square foot and drops the Brazilian ART/RRT filing,
 * which has no counterpart in a US or UK practice.
 */

type PipelineCard = { age: string; client: string; name: string; value: string }
type PipelineColumn = { cards: readonly PipelineCard[]; title: string; tone: string }
type Line = { label: string; value: string }
type CatalogItem = { name: string; price: string; unit: string }
type Step = { label: string; tone: string }

type ProposalsContent = {
  eyebrow: string
  heading: string
  intro: string
  pipelineTitle: string
  pipelineBadge: string
  newProposalLabel: string
  pipeline: readonly PipelineColumn[]
  publicEyebrow: string
  publicTitle: string
  publicMeta: string
  scopeLabel: string
  publicLines: readonly Line[]
  totalLabel: string
  totalValue: string
  paymentTerms: string
  discountNote: string
  acceptLabel: string
  changeLabel: string
  lifecycle: readonly Step[]
  catalogEyebrow: string
  catalogTitle: string
  catalogBadge: string
  catalogItems: readonly CatalogItem[]
  perLabel: string
}

const CONTENT: Record<MarketingLocale, ProposalsContent> = {
  en: {
    acceptLabel: 'Accept',
    catalogBadge: '42 standard services · editable',
    catalogEyebrow: 'PRESETS',
    catalogItems: [
      { name: 'Fee per sq ft (residential)', price: '$14', unit: 'sq ft' },
      { name: 'Fee per sq ft (commercial)', price: '$16', unit: 'sq ft' },
      { name: 'Measured survey', price: '$1,200', unit: 'visit' },
      { name: 'Concept design 2D + 3D', price: '$9,600', unit: 'project' },
      { name: 'Construction documents', price: '$18,400', unit: 'project' },
      { name: 'Millwork detailing', price: '$2,400', unit: 'room' },
      { name: 'Permit filing', price: '$540', unit: 'filing' },
      { name: 'Construction administration', price: '$1,125', unit: 'visit' },
      { name: 'Exterior rendering', price: '$380', unit: 'image' },
    ],
    catalogTitle: 'A standard catalog, already loaded',
    changeLabel: 'Request changes',
    discountNote: '7% off paid in full',
    eyebrow: 'PROPOSALS',
    heading: 'From first conversation to signed contract, without leaving Planno',
    intro:
      'Build the proposal from your own catalog, send a link the client opens on their phone, and see exactly when they read it, accepted it, or asked for changes.',
    lifecycle: [
      { label: 'Lead', tone: '#5b8def' },
      { label: 'Brief', tone: '#e8a13d' },
      { label: 'Proposal sent', tone: '#7a6cb6' },
      { label: 'Client opened', tone: '#d96fa3' },
      { label: 'Accepted', tone: '#7bb86b' },
      { label: 'Project + receivables', tone: '#1d7a4c' },
    ],
    newProposalLabel: '+ New proposal',
    paymentTerms: '20% deposit + 6 × $6,400',
    perLabel: 'per',
    pipeline: [
      {
        cards: [
          {
            age: '2 days',
            client: 'The Vaughans',
            name: 'Vaughan Residence',
            value: '$48,000',
          },
        ],
        title: 'Draft',
        tone: '#a8a8ad',
      },
      {
        cards: [
          { age: '5 days', client: 'Carmine Studio', name: 'Foundry Lane', value: '$32,500' },
          { age: '8 days', client: 'The Suttons', name: 'Coast House', value: '$86,000' },
        ],
        title: 'Sent',
        tone: '#5b8def',
      },
      {
        cards: [{ age: 'today', client: 'Aurora Dev.', name: 'Aurora Tower', value: '$220,000' }],
        title: 'Accepted',
        tone: '#7bb86b',
      },
      {
        cards: [{ age: '1 day', client: 'M. Ellery', name: 'Marlow Loft', value: '$24,000' }],
        title: 'Changes requested',
        tone: '#e8a13d',
      },
    ],
    pipelineBadge: '$410k in play',
    pipelineTitle: 'Proposals',
    publicEyebrow: 'WHITFIELD STUDIO · PROPOSAL #042',
    publicLines: [
      { label: 'Measured survey, 2,600 sq ft', value: '$4,800' },
      { label: 'Concept design 2D + 3D', value: '$9,600' },
      { label: 'Full construction documents', value: '$18,400' },
      { label: 'Consultant coordination', value: '$6,200' },
      { label: 'Construction administration (8 visits)', value: '$9,000' },
    ],
    publicMeta: 'Valid through Jun 18 · The Vaughans',
    publicTitle: 'Vaughan Residence',
    scopeLabel: 'SCOPE',
    totalLabel: 'Total',
    totalValue: '$48,000',
  },
  'pt-BR': {
    acceptLabel: 'Aceitar',
    catalogBadge: '42 serviços padrão · editáveis',
    catalogEyebrow: 'PREDEFINIÇÕES',
    catalogItems: [
      { name: 'Honorários por m² (residencial)', price: 'R$ 380', unit: 'm²' },
      { name: 'Honorários por m² (comercial)', price: 'R$ 420', unit: 'm²' },
      { name: 'Levantamento métrico', price: 'R$ 1.200', unit: 'visita' },
      { name: 'Estudo preliminar 2D + 3D', price: 'R$ 9.600', unit: 'projeto' },
      { name: 'Projeto executivo', price: 'R$ 18.400', unit: 'projeto' },
      { name: 'Detalhamento de marcenaria', price: 'R$ 2.400', unit: 'ambiente' },
      { name: 'ART / RRT', price: 'R$ 540', unit: 'emissão' },
      { name: 'Acompanhamento de obra', price: 'R$ 1.125', unit: 'visita' },
      { name: 'Render externo', price: 'R$ 380', unit: 'imagem' },
    ],
    catalogTitle: 'Catálogo nacional já carregado',
    changeLabel: 'Solicitar alteração',
    discountNote: 'à vista 7% off',
    eyebrow: 'PROPOSTAS',
    heading: 'Da primeira conversa ao contrato assinado, sem sair do Planno',
    intro:
      'Monte a proposta a partir do seu catálogo, mande um link que o cliente abre no celular, e veja exatamente quando ele leu, aceitou ou pediu alteração.',
    lifecycle: [
      { label: 'Lead', tone: '#5b8def' },
      { label: 'Briefing', tone: '#e8a13d' },
      { label: 'Proposta enviada', tone: '#7a6cb6' },
      { label: 'Cliente abriu', tone: '#d96fa3' },
      { label: 'Aceita', tone: '#7bb86b' },
      { label: 'Projeto + recebíveis', tone: '#1d7a4c' },
    ],
    newProposalLabel: '+ Nova proposta',
    paymentTerms: 'Entrada 20% + 6× R$ 6.400',
    perLabel: 'por',
    pipeline: [
      {
        cards: [
          {
            age: '2 dias',
            client: 'Família Vieira',
            name: 'Residência Vieira',
            value: 'R$ 48.000',
          },
        ],
        title: 'Rascunho',
        tone: '#a8a8ad',
      },
      {
        cards: [
          { age: '5 dias', client: 'Studio Carmim', name: 'Loja Centro', value: 'R$ 32.500' },
          { age: '8 dias', client: 'Família Souto', name: 'Casa de Praia', value: 'R$ 86.000' },
        ],
        title: 'Enviada',
        tone: '#5b8def',
      },
      {
        cards: [
          { age: 'hoje', client: 'Aurora Inc.', name: 'Edifício Aurora', value: 'R$ 220.000' },
        ],
        title: 'Aceita',
        tone: '#7bb86b',
      },
      {
        cards: [
          { age: '1 dia', client: 'M. Almeida', name: 'Reforma Almeida', value: 'R$ 24.000' },
        ],
        title: 'Alteração',
        tone: '#e8a13d',
      },
    ],
    pipelineBadge: 'R$ 410k em jogo',
    pipelineTitle: 'Propostas',
    publicEyebrow: 'ESTÚDIO LOPES · PROPOSTA #042',
    publicLines: [
      { label: 'Levantamento métrico, 240 m²', value: 'R$ 4.800' },
      { label: 'Estudo preliminar 2D + 3D', value: 'R$ 9.600' },
      { label: 'Projeto executivo completo', value: 'R$ 18.400' },
      { label: 'Compatibilização dos complementares', value: 'R$ 6.200' },
      { label: 'Acompanhamento de obra (8 visitas)', value: 'R$ 9.000' },
    ],
    publicMeta: 'Válida até 18 jun · Família Vieira',
    publicTitle: 'Residência Vieira',
    scopeLabel: 'ESCOPO',
    totalLabel: 'Valor total',
    totalValue: 'R$ 48.000',
  },
}

const ProposalPipeline = ({ content }: { content: ProposalsContent }) => (
  <div className="overflow-hidden rounded-2xl border border-[#ececec] bg-white shadow-[0_24px_60px_-26px_rgba(20,30,60,0.28)]">
    <div className="flex items-center justify-between border-[#f1f1f1] border-b px-5 py-3">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-[#1c1c20] text-[13px]">{content.pipelineTitle}</span>
        <span className="rounded-md bg-[#f1eaff] px-2 py-1 font-medium text-[#7a6cb6] text-[10px]">
          {content.pipelineBadge}
        </span>
      </div>
      <span className="rounded-md bg-[#1d1d20] px-2 py-1 text-[10.5px] text-white">
        {content.newProposalLabel}
      </span>
    </div>
    <div className="grid grid-cols-4 gap-2 p-3">
      {content.pipeline.map((col) => (
        <div className="rounded-xl bg-[#fafafa] p-2.5" key={col.title}>
          <div className="flex items-center gap-1.5 px-1 pb-2">
            <span className="size-[7px] rounded-full" style={{ background: col.tone }} />
            <span className="font-semibold text-[#1c1c20] text-[11px]">{col.title}</span>
            <span className="ml-auto text-[#9a9aa0] text-[10px]">{col.cards.length}</span>
          </div>
          <div className="space-y-2">
            {col.cards.map((card) => (
              <div className="rounded-lg border border-[#ececec] bg-white p-2.5" key={card.name}>
                <p className="font-medium text-[#1c1c20] text-[11.5px] leading-snug">{card.name}</p>
                <p className="mt-0.5 text-[#9a9aa0] text-[10px]">{card.client}</p>
                <div className="mt-2 flex items-center justify-between">
                  <span className="font-semibold text-[#43434a] text-[11px]">{card.value}</span>
                  <span className="text-[#a8a8ad] text-[9.5px]">{card.age}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
)

const PublicProposal = ({ content }: { content: ProposalsContent }) => (
  <div className="overflow-hidden rounded-2xl border border-[#e7e3f4] bg-white shadow-[0_24px_60px_-22px_rgba(40,30,90,0.32)]">
    <div className="border-[#f1f1f1] border-b bg-gradient-to-br from-[#f5f3ff] to-white px-5 py-4">
      <p className="text-[#7a6cb6] text-[10.5px] tracking-[0.18em]">{content.publicEyebrow}</p>
      <p className="mt-2 font-bold text-[#1c1c20] text-[19px] leading-tight">
        {content.publicTitle}
      </p>
      <p className="mt-1 text-[#6c6c72] text-[11.5px]">{content.publicMeta}</p>
    </div>
    <div className="px-5 py-4">
      <p className="text-[#9a9aa0] text-[10.5px] tracking-[0.16em]">{content.scopeLabel}</p>
      <div className="mt-2 space-y-1.5">
        {content.publicLines.map((line) => (
          <div className="flex items-center justify-between text-[11.5px]" key={line.label}>
            <span className="text-[#43434a]">{line.label}</span>
            <span className="font-medium text-[#1c1c20]">{line.value}</span>
          </div>
        ))}
      </div>
      <div className="mt-4 rounded-xl bg-[#f6f4fc] p-3">
        <div className="flex items-center justify-between">
          <span className="text-[#6c6c72] text-[11px]">{content.totalLabel}</span>
          <span className="font-bold text-[#1c1c20] text-[18px]">{content.totalValue}</span>
        </div>
        <div className="mt-1 flex items-center justify-between text-[10.5px]">
          <span className="text-[#9a9aa0]">{content.paymentTerms}</span>
          <span className="text-[#7a6cb6]">{content.discountNote}</span>
        </div>
      </div>
      <div className="mt-4 flex gap-2">
        <span className="flex-1 rounded-lg bg-[#7bb86b] py-2 text-center font-semibold text-[11px] text-white">
          {content.acceptLabel}
        </span>
        <span className="flex-1 rounded-lg border border-[#ececec] py-2 text-center font-medium text-[#43434a] text-[11px]">
          {content.changeLabel}
        </span>
      </div>
    </div>
  </div>
)

export const ProposalsSection = () => {
  const content = useLocalized(CONTENT)

  return (
    <section
      className="relative overflow-hidden py-28"
      style={{ background: 'linear-gradient(180deg, #ffffff 0%, #f4f0fb 40%, #eee8f9 100%)' }}
    >
      <TruchetField
        className="absolute inset-0 h-full w-full text-[#0b0b0d] opacity-[0.05]"
        seed="proposals"
      />
      <div className="relative mx-auto max-w-[1120px] px-6">
        <ScrollReveal className="text-center">
          <p className="text-[#7a6cb6] text-[12px] tracking-[0.2em]">{content.eyebrow}</p>
          <h2 className="mx-auto mt-3 max-w-[820px] font-display text-[#0b0b0d] text-[clamp(40px,6vw,58px)] leading-[1.06] tracking-[-0.01em]">
            {content.heading}
          </h2>
          <p className="mx-auto mt-5 max-w-[640px] text-[#6c6c72] text-[clamp(16px,2.2vw,20px)]">
            {content.intro}
          </p>
        </ScrollReveal>

        {/* Asymmetric overlap, pipeline + floating public proposal on the right */}
        <div className="relative mt-16">
          <ScrollReveal>
            <ProposalPipeline content={content} />
          </ScrollReveal>
          <ScrollReveal
            className="-bottom-12 hidden md:absolute md:right-[-20px] md:block md:w-[320px]"
            delay={140}
          >
            <PublicProposal content={content} />
          </ScrollReveal>
        </div>

        {/* Mobile fallback for the public proposal */}
        <ScrollReveal className="mt-6 md:hidden" delay={140}>
          <PublicProposal content={content} />
        </ScrollReveal>

        {/* Lifecycle strip, horizontal, taking the role of the "3 cards" row */}
        <ScrollReveal className="mt-20 md:mt-28" delay={160}>
          <div className="overflow-x-auto">
            <div className="flex min-w-[760px] items-center gap-3">
              {content.lifecycle.map((step, i) => (
                <span className="flex flex-1 items-center gap-3" key={step.label}>
                  <span className="flex flex-1 items-center gap-2 rounded-full bg-white px-4 py-2.5 shadow-sm">
                    <span
                      aria-hidden="true"
                      className="size-[8px] shrink-0 rounded-full"
                      style={{ background: step.tone }}
                    />
                    <span className="font-semibold text-[#0b0b0d] text-[12.5px]">{step.label}</span>
                    <span className="ml-auto text-[#9a9aa0] text-[10.5px]">{`${i + 1}/${content.lifecycle.length}`}</span>
                  </span>
                  {i < content.lifecycle.length - 1 && <span className="text-[#9a9aa0]">→</span>}
                </span>
              ))}
            </div>
          </div>
        </ScrollReveal>

        {/* Service catalog, wide table styled differently from any prior section */}
        <ScrollReveal className="mt-10" delay={180}>
          <div className="overflow-hidden rounded-2xl border border-[#e7e3f4] bg-white">
            <div className="flex items-baseline justify-between border-[#f1f1f1] border-b px-6 py-4">
              <div>
                <p className="text-[#7a6cb6] text-[11px] tracking-[0.2em]">
                  {content.catalogEyebrow}
                </p>
                <p className="mt-1 font-bold text-[#0b0b0d] text-[20px]">{content.catalogTitle}</p>
              </div>
              <span className="rounded-md bg-[#f6f4fc] px-3 py-1.5 text-[#7a6cb6] text-[11px]">
                {content.catalogBadge}
              </span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3">
              {content.catalogItems.map((item, i) => (
                <div
                  className={`flex items-center justify-between gap-3 px-6 py-3 ${i % 3 === 0 ? '' : 'md:border-[#f4f4f4] md:border-l'} ${i >= 3 ? 'border-[#f4f4f4] border-t md:border-t' : ''}`}
                  key={item.name}
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium text-[#1c1c20] text-[12.5px]">{item.name}</p>
                    <p className="text-[#9a9aa0] text-[10.5px]">
                      {content.perLabel} {item.unit}
                    </p>
                  </div>
                  <span className="font-semibold text-[#7a6cb6] text-[12.5px]">{item.price}</span>
                </div>
              ))}
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  )
}
